import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [company, setCompany] = useState(null);
  const [access, setAccess] = useState(null);

  const loadMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
      setAccess(data.access);
    } catch {
      setUser(false);
      setCompany(null);
      setAccess(null);
    }
  };

  const refreshAccess = loadMe;

  useEffect(() => {
    if (localStorage.getItem("qc_token")) loadMe();
    else { setUser(false); setCompany(null); setAccess(null); }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.two_factor) return { twoFactor: true, email: data.email };
    if (data.access_token) localStorage.setItem("qc_token", data.access_token);
    await loadMe();
    return { twoFactor: false };
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    if (data.access_token) localStorage.setItem("qc_token", data.access_token);
    await loadMe();
  };

  const resendOtp = async (email) => {
    await api.post("/auth/resend-otp", { email });
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (email, token, password) => {
    const { data } = await api.post("/auth/reset-password", { email, token, password });
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.two_factor) return { twoFactor: true, email: data.email };
    if (data.access_token) localStorage.setItem("qc_token", data.access_token);
    await loadMe();
    return { twoFactor: false };
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* best effort */ }
    localStorage.removeItem("qc_token");
    setUser(false);
    setCompany(null);
    setAccess(null);
  };

  // Auto logout after 30 min of inactivity
  useEffect(() => {
    if (!user || user === false) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { logout(); }, IDLE_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, company, access, refreshAccess, login, verifyOtp, resendOtp, forgotPassword, resetPassword, register, logout, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
